import { Badge, Tooltip } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as ReactBootstrap from "react-bootstrap";
import { Calendar } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useSelector } from "react-redux";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "../../../assets/css/accountmetrics.scss";
import cross from "../../../assets/icons/cross-close.svg";
import MobileTabs from "../../../reusableComponents/MobileTabs/MobileTabs";
import { dollarUS } from "../../../utilities/formatters";
import { formatDateInTimeZone } from "../../../utilities/string";
import NewTradingHistory from "../NewTradingHistory";
import TradesLots from "../TradesLots";
import AdvancedStaistics, { CountryData } from "../AdvancedStaistics";
import dayjs from "dayjs";

const AdvancedInsights = ({
  data,
  AdvStats,
  TradingHistory,
  TradeJournalNew,
}) => {
  // Memoized newData derived from TradeJournalNew
  const newData = useMemo(() => {
    if (!TradeJournalNew?.data) {
      console.log("TradeJournalNew.data is empty or undefined");
      return [];
    }
    const result = TradeJournalNew.data.map((item) => ({
      ...item,
      date: item.date || dayjs(item?.tradeDateTime).utc().format("YYYY-MM-DDTHH:mm:ss"),
      profit: Number(item.profit || item?.netProfit || 0),
      commission: Number(item.commission || item?.executionFee || 0),
      volume: Number(item.volume || item?.lots/2 || 0),
    }));
    console.log("Computed newData:", result);
    return result;
  }, [TradeJournalNew?.data]);

  const [show, setShow] = useState("AdvancedStaistics");
  const [activeTradeCard, setActiveTradeCard] = useState(null);
  const [profileActive, setProfileActive] = useState(1);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTradeDate, setSelectedTradeDate] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [events, setEvents] = useState({});

  // Compute trading events based on newData
  useEffect(() => {
    const tradingEvents = {};
    newData.forEach((trade) => {
      const tradeDate = moment(trade.date).format("YYYY-MM-DD");
      if (!tradingEvents[tradeDate]) {
        tradingEvents[tradeDate] = {
          trades: [],
          totalProfit: 0,
          tradeCount: 0,
          volume: 0,
          totalCommission: 0,
        };
      }
      tradingEvents[tradeDate].trades.push(trade);
      tradingEvents[tradeDate].totalProfit += trade.profit || 0;
      tradingEvents[tradeDate].tradeCount += 1;
      tradingEvents[tradeDate].volume += trade.volume || 0;
      tradingEvents[tradeDate].totalCommission += trade.commission || 0;
    });
    setEvents(tradingEvents);
  }, [newData]);

  // Filter trades for ±2 days around selectedDate
  const filteredTrades = useMemo(() => {
    const result = [];
    const currentDate = moment(selectedDate, "YYYY-MM-DD");
    for (let i = -2; i <= 2; i++) {
      const date = currentDate.clone().add(i, "days").format("YYYY-MM-DD");
      result.push({
        [date]: events[date] || { trades: [], totalProfit: 0, tradeCount: 0, volume: 0, totalCommission: 0 },
      });
    }
    return result;
  }, [selectedDate, events]);

  const handleDateChange = (date) => {
    const formattedDate = moment(date).format("YYYY-MM-DD");
    setSelectedDate(formattedDate);
    setSelectedTradeDate(date);
    setActiveTradeCard(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTrade(null);
    setSelectedTradeDate(null);
  };

  // Calendar tile content for highlighting trade days
  const tileContent = ({ date }) => {
    const currentDate = moment(date).format("YYYY-MM-DD");
    if (events[currentDate]?.tradeCount > 0) {
      const totalProfit = events[currentDate].totalProfit;
      return (
        <Tooltip
          title={totalProfit > 0 ? "Profit Day" : totalProfit < 0 ? "Loss Day" : "Trade Day"}
          style={{ marginLeft: "7px" }}
        >
          <Badge
            color={totalProfit > 0 ? "success" : totalProfit < 0 ? "error" : "info"}
            badgeContent=" "
            variant="dot"
          />
        </Tooltip>
      );
    }
    return null;
  };

  const ExtraInforChart = ({ events, selectedDate }) => {
    console.log("ExtraInforChart rendering with selectedDate:", selectedDate);
    const tradeData = events[selectedDate] || { trades: [], totalProfit: 0, tradeCount: 0, volume: 0, totalCommission: 0 };
    const totalWinners = tradeData.trades
      .filter((item) => item.profit > 0)
      .reduce((sum, item) => sum + item.profit, 0);
    const totalLosers = tradeData.trades
      .filter((item) => item.profit < 0)
      .reduce((sum, item) => sum + item.profit, 0);

    return (
      <div className="extra_information">
        {[
          { data: tradeData.tradeCount, text: "Total Trades" },
          { data: tradeData.totalProfit, text: "Gross P&L" },
          { data: tradeData.volume, text: "Volume" },
          { data: totalWinners, text: "Winners" },
          { data: totalLosers, text: "Losers" },
          { data: tradeData.totalCommission, text: "Commission" },
        ].map((item, index) => (
          <div key={index}>
            <p
              style={{
                color: item.text === "Winners" ? "#0CCE6B" : item.text === "Losers" ? "#FD504F" : "",
              }}
            >
              {item.text}
            </p>
            <span>
              {item.text === "Commission" || item.text === "Gross P&L" || item.text === "Losers" || item.text === "Winners"
                ? dollarUS(item.data)
                : isNaN(parseFloat(item.data).toFixed(2))
                ? 0
                : item.text === "Total Trades"
                ? item.data
                : parseFloat(item.data).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const tabItems = [
    { name: "Advanced Statistics", value: 1 },
    { name: "Trades by Lots", value: 2 },
  ];

  return (
    <div>
      <div className="d-flex accountStatisticsContent">
        {isModalOpen && (
          <TradeModal
            date={selectedTradeDate}
            isOpen={isModalOpen}
            closeModal={closeModal}
            trade={selectedTrade}
          />
        )}

        <div className={`InsightsTabTable mbb-20 ${profileActive === 2 ? "profileTabCountry" : ""}`} style={{ order: 3, width: "100%" }}>
          <div className="profileTabination">
            <div className="leftSection">
              <div
                onClick={() => setProfileActive(1)}
                className={`profileTabination-item noFill ${profileActive === 1 ? "active" : ""}`}
              >
                <div className="svgCon">
                  <svg
                    width="20"
                    height="17"
                    viewBox="0 0 20 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 11V12.8C1 13.92 1 14.48 1.218 14.908C1.40974 15.2843 1.71569 15.5903 2.092 15.782C2.519 16 3.079 16 4.197 16H19M1 11V1M1 11L4.853 7.79L4.857 7.787C5.554 7.206 5.903 6.915 6.282 6.797C6.72913 6.65719 7.21116 6.67878 7.644 6.858C8.011 7.011 8.332 7.332 8.976 7.976L8.982 7.982C9.636 8.636 9.963 8.964 10.336 9.115C10.7774 9.29489 11.2686 9.31121 11.721 9.161C12.104 9.033 12.454 8.727 13.154 8.115L19 3"
                      stroke={profileActive === 1 ? "#fff" : "grey"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                Advanced Statistics
              </div>
              <div
                onClick={() => setProfileActive(2)}
                className={`profileTabination-item ${profileActive === 2 ? "active" : ""}`}
              >
                <div className="svgCon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_220_7770)">
                      <path
                        d="M17.8224 0L15.1855 3.33341L12.7129 2.07167L12.1252 1.78594L11.7485 2.333L9.18169 6.23862L6.80396 4.42829L6.35644 4.09533L5.90892 4.42905L3.17784 6.49996L0.517586 5.83327L0.140886 7.30988L3.15449 8.0718L3.50783 8.16704L3.79036 7.95218L6.35644 6.00013L8.92252 7.95294L9.55839 8.42838L10.0059 7.7617L12.6428 3.76161L15.0673 4.97611L15.6097 5.23821L19 0.952402L17.8224 0ZM12.4304 9.85774L11.7952 10.6669L9.2284 13.9051L6.80396 12.0475L6.35644 11.7145L5.90892 12.0483L3.27202 14.0483L0.659225 12.7378L0 14.1192L3.0136 15.643L3.4129 15.8335L3.7896 15.5722L6.35644 13.6194L8.92252 15.5722L9.51092 16.0004L9.95844 15.4289L12.3128 12.4529L14.7614 16.1428L15.3505 17L15.9856 16.1908L18.9992 12.3812L17.8224 11.4288L15.4681 14.4049L13.0195 10.7149L12.4304 9.85774Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_220_7770">
                        <rect width="19" height="19" fill="white" transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                Trades by Lots
              </div>
              <div className="mobileTabOption">
                <MobileTabs activeTab={profileActive} setActiveTab={setProfileActive} tabItems={tabItems} />
              </div>
            </div>
            <div className="rightSection">
              {profileActive === 2 && (
                <div className="countryData">
                  <div className="advStatsCountryContainer">
                    <div className="advStatsCountryWrapper">
                      <CountryData AdvStats={AdvStats} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="advStatContainer" style={{ width: "100%" }}>
            {profileActive === 1 && (
              <AdvancedStaistics data={data} AdvStats={AdvStats} />
            )}
            {profileActive === 2 && (
              <TradesLots
                TradesLotsPerDay={AdvStats?.["trades_and_lots_per_day "]}
                LotsPerTrade={AdvStats?.lots_per_trade}
                ConcurrentTrades={AdvStats?.concurrent_trades_lots}
                RiskPerTrade={AdvStats?.risk_per_trade}
              />
            )}
          </div>
        </div>

        {profileActive === 1 && (
          <>
            <div className="tradingActivityContainer">
              <NewTradingHistory add={false} TradingHistory={TradingHistory} />
            </div>
            <div className="tradesByDate">
              <div className="left">
                <Calendar onChange={handleDateChange} tileContent={tileContent} />
              </div>
              <div className="right">
                <div className="top">
                  <p className="day_name">
                    {moment(selectedTradeDate || Date.now()).format("dddd, YYYY")}
                  </p>
                </div>
                <div className="mid">
                  <div className="tradeCards_wrapper">
                    {filteredTrades.map((item, index) => (
                      <TradeCard
                        key={index}
                        date={item}
                        setIsModalOpen={setIsModalOpen}
                        setSelectedTrade={setSelectedTrade}
                        setSelectedTradeDate={setSelectedTradeDate}
                        index={index}
                        activeTradeCard={activeTradeCard}
                        setActiveTradeCard={setActiveTradeCard}
                        events={events}
                        setSelectedDate={setSelectedDate} // Pass setSelectedDate
                      />
                    ))}
                  </div>
                  <ExtraInforChart events={events} selectedDate={selectedDate} />
                </div>
                <div className="bottom"></div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default AdvancedInsights

const TradeCard = ({
  date,
  setIsModalOpen,
  setSelectedTrade,
  setSelectedTradeDate,
  index,
  activeTradeCard,
  setActiveTradeCard,
  events,
  setSelectedDate,
}) => {
  const key = Object.keys(date)[0];
  const data = date[key];
  const day = moment(key).format("DD");
  const formattedDate = moment(key).format("YYYY-MM-DD");

  // Format price for display
  const formatPrice = (price) => {
    if (Math.abs(price) >= 1000) {
      return (price < 0 ? "-$" : "$") + (Math.abs(price) / 1000).toFixed(2) + "K";
    }
    return (price < 0 ? "-$" : "$") + Math.abs(price).toFixed(2);
  };

  const totalProfit = data.totalProfit || 0;
  const tradeCount = data.tradeCount || 0;
  const symbol = data.trades?.[0]?.symbol || "N/A";

  return (
    <div
      className={`tradeCard ${
        activeTradeCard === index
          ? totalProfit > 0
            ? "profit_trade_card"
            : totalProfit < 0
            ? "loss_trade_card"
            : ""
          : ""
      }`}
      onClick={() => {
        console.log("Clicked TradeCard date:", key, "events data:", events[key]);
        setSelectedTradeDate(new Date(key));
        setSelectedTrade(data);
        setSelectedDate(formattedDate); // Update selectedDate
        setIsModalOpen(tradeCount > 0);
        setActiveTradeCard(index);
      }}
    >
      <div className="top">
        {/* <div className="left">{symbol}</div> */}
        <div className={`right ${totalProfit < 0 ? "loss" : ""}`}>
          <p>
            <span>{day}</span>
          </p>
        </div>
      </div>
      <div className="mid" style={{ width: "100%", display: "flex", justifyContent: "end" }} />
      <div>
        <p
          className={`trade_cart_profit_loss ${
            totalProfit > 0 ? "profit_tradeCard" : totalProfit < 0 ? "loss_tradeCard" : ""
          }`}
        >
          {tradeCount > 0 ? formatPrice(totalProfit) : "$0.00"}
        </p>
      </div>
    </div>
  );
};

const TradeModal = ({ isOpen, closeModal, trade, date }) => {
  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeModal]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("HH:mm:ss");
  };

  return (
    <div className={`trade-modal ${isOpen ? "open" : ""}`}>
      <div className="modal_content" ref={modalRef}>
        <div className="modal_header">
          <p>{moment(date).format("ll")}</p>
          <button className="close-button" onClick={closeModal}>
            <img src={cross} alt="Close" />
          </button>
        </div>
        {trade?.trades?.length > 0 ? (
          <div style={{ marginTop: "20px" }}>
            <div className="PageContainer LeaderboardPage PaymentHistoryPage mtt-20">
              <ReactBootstrap.Table className="LeaderboardTable">
                <thead>
                  <tr style={{ textWrap: "nowrap" }}>
                    <th>Action</th>
                    <th>Commission</th>
                    <th>Date Close</th>
                    <th>Date Open</th>
                    <th>Login</th>
                    <th>Position</th>
                    <th>Price Avg Buy</th>
                    <th>Price Avg Sell</th>
                    <th>Profit</th>
                    <th>Stop Loss</th>
                    <th>Swap</th>
                    <th>Symbol</th>
                    <th>Take Profit</th>
                    <th>Volume Buy</th>
                    <th>Volume Sell</th>
                  </tr>
                </thead>
                <tbody>
                  {trade.trades.map((trade, index) => (
                    <tr key={index}>
                      <td>{trade.action || "N/A"}</td>
                      <td>{dollarUS(trade.commission || 0)}</td>
                      <td>{formatDate(trade.dateClose)}</td>
                      <td>{formatDate(trade.date)}</td>
                      <td>{trade.login || "N/A"}</td>
                      <td>{trade.position || "N/A"}</td>
                      <td>{trade.priceAvgBuy || "N/A"}</td>
                      <td>{trade.priceAvgSell || "N/A"}</td>
                      <td className={trade.profit > 0 ? "green" : trade.profit < 0 ? "red" : ""}>
                        {dollarUS(trade.profit || 0)}
                      </td>
                      <td>{trade.stopLoss || "N/A"}</td>
                      <td>{trade.swapSum || "N/A"}</td>
                      <td>{trade.symbol || "N/A"}</td>
                      <td>{trade.takeProfit || "N/A"}</td>
                      <td>{trade.volumeSumBuy || trade.volume || 0}</td>
                      <td>{trade.volumeSumSell || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </ReactBootstrap.Table>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <p>No trades available for this date.</p>
          </div>
        )}
      </div>
    </div>
  );
};
